import React from 'react';
import { DateTime } from 'luxon';
import { CallDirection } from '../../../../../dialer/types/call-logs';
import { OutcomeSentimentVariant } from '../types/tasker-call-insights';
import { OutgoingCallIcon } from '../../../../../../shared/svg/outgoing-call';
import { IncomingCallIcon } from '../../../../../../shared/svg/incoming-call';
import { MissedCallIcon } from '../../../../../../shared/svg/missed-call';

export const getOrdinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const parseIso = (iso?: string, timeZone?: string): DateTime | null => {
  if (!iso) return null;
  const dt = DateTime.fromISO(iso, {
    zone: timeZone || DateTime.local().zoneName,
  });
  return dt.isValid ? dt : null;
};

/** "15th Oct 2025" */
export const formatCallDate = (iso?: string, timeZone?: string): string => {
  const dt = parseIso(iso, timeZone);
  if (!dt) return '';
  return `${dt.day}${getOrdinalSuffix(dt.day)} ${dt.toFormat('LLL yyyy')}`;
};

/** "10:00 AM" */
export const formatCallTime = (iso?: string, timeZone?: string): string => {
  const dt = parseIso(iso, timeZone);
  if (!dt) return '';
  return dt.toFormat('hh:mm a');
};

/** "2m 3s" — converts seconds to minutes and seconds display */
export const formatCallDuration = (durationInSeconds?: number | null): string => {
  const totalSeconds = durationInSeconds ?? 0;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

/** "Thu, Sept 11, 2025 - 10:35 AM" */
export const formatSummaryTimestamp = (iso?: string, timeZone?: string): string => {
  const dt = parseIso(iso, timeZone);
  if (!dt) return '';
  return dt.toFormat('EEE, LLL d, yyyy - hh:mm a');
};

/** "Thu, Sept 11, 2025 · 10:35 AM" */
export const formatNoteTimestamp = (iso?: string, timeZone?: string): string => {
  const dt = parseIso(iso, timeZone);
  if (!dt) return '';
  return dt.toFormat('EEE, LLL d, yyyy · hh:mm a');
};

export const getInitials = (firstName?: string, lastName?: string): string => {
  const f = firstName?.trim()?.[0] || '';
  const l = lastName?.trim()?.[0] || '';
  return `${f}${l}`.toUpperCase() || '—';
};

export const getCallDirection = (
  type: string,
  isAnswered: boolean,
): CallDirection => {
  const normalized = (type || '').toLowerCase();
  if (normalized === 'inbound' || normalized === 'incoming') {
    return isAnswered ? CallDirection.Incoming : CallDirection.Missed;
  }
  if (normalized === 'missed') return CallDirection.Missed;
  if (normalized === 'missed-with-voicemail') {
    return CallDirection.MissedWithVoicemail;
  }
  return CallDirection.Outgoing;
};

export const getCallDirectionIcon = (direction: CallDirection) => {
  switch (direction) {
    case CallDirection.Outgoing:
      return React.createElement(OutgoingCallIcon, { width: 20, height: 20 });
    case CallDirection.Incoming:
      return React.createElement(IncomingCallIcon, { width: 20, height: 20 });
    case CallDirection.Missed:
    case CallDirection.MissedWithVoicemail:
      return React.createElement(MissedCallIcon, { width: 20, height: 20 });
    default:
      return null;
  }
};

export const getOutcomeSentimentVariant = (
  sentiment?: string,
): OutcomeSentimentVariant => {
  const s = (sentiment || '').toLowerCase();
  if (s === 'positive') return 'positive';
  if (s === 'negative') return 'negative';
  return 'neutral';
};

/** Path segment is CallLog row id (`call.id`), not prospect id. */
export const buildCallLogRedirectUrl = (callLogRowId?: number): string => {
  if (!callLogRowId) return '/dialer/call-logs';
  return `/dialer/call-logs/${callLogRowId}/calls?callId=${encodeURIComponent(
    String(callLogRowId),
  )}&type=all`;
};
